/**
 * Spot WebSocket Market Streams
 *
 * Access market data, manage accounts, and trade on Binance Spot.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import {
    WebsocketStreamsBase,
    WebsocketStream,
    assertParamExists,
    replaceWebsocketStreamsPlaceholders,
    createStreamHandler,
} from '@binance/common';
import type {
    AggTradeResponse,
    AllMarketRollingWindowTickerResponse,
    AllMiniTickerResponse,
    AvgPriceResponse,
    BlockTradeResponse,
    BookTickerResponse,
    DiffBookDepthResponse,
    KlineOffsetResponse,
    KlineResponse,
    MiniTickerResponse,
    PartialBookDepthResponse,
    ReferencePriceResponse,
    RollingWindowTickerResponse,
    TickerResponse,
    TradeResponse,
} from '../types';

const ApiParamCreator = function () {
    return {
        /**
         * The Aggregate Trade Streams push trade information that is aggregated
         * for a single taker order.
         *
         * Update Speed: Real-time
         *
         * @summary Aggregate Trade Streams
         * @param {string} symbol Symbol to query
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        aggTrade: (symbol: string, id?: string): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('aggTrade', 'symbol', symbol);

            return replaceWebsocketStreamsPlaceholders('/<symbol>@aggTrade'.slice(1), {
                symbol,
                id,
            });
        },
        /**
         * Rolling window ticker statistics for all market symbols, computed over
         * multiple windows.
         *
         * Note that only tickers that have changed will be present in the array.
         *
         * Update Speed: 1000ms
         *
         * @summary All Market Rolling Window Statistics Streams
         * @param {AllMarketRollingWindowTickerWindowSizeEnum} windowSize
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        allMarketRollingWindowTicker: (
            windowSize: AllMarketRollingWindowTickerWindowSizeEnum,
            id?: string
        ): string => {
            // verify required parameter 'windowSize' is not null or undefined
            assertParamExists('allMarketRollingWindowTicker', 'windowSize', windowSize);

            return replaceWebsocketStreamsPlaceholders('/!ticker_<windowSize>@arr'.slice(1), {
                windowSize,
                id,
            });
        },
        /**
         * 24hr rolling window mini-ticker statistics for all symbols that changed
         * in an array. These are NOT the statistics of the UTC day, but a 24hr
         * rolling window for the previous 24hrs. Note that only tickers that have
         * changed will be present in the array.
         *
         * Update Speed: 1000ms
         *
         * @summary All Market Mini Tickers Stream
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        allMiniTicker: (id?: string): string => {
            return replaceWebsocketStreamsPlaceholders('/!miniTicker@arr'.slice(1), { id });
        },
        /**
         * Average price streams push changes in the average price over a fixed time interval.
         *
         * Update Speed: 1000ms
         *
         * @summary Average Price
         * @param {string} symbol Symbol to query
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        avgPrice: (symbol: string, id?: string): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('avgPrice', 'symbol', symbol);

            return replaceWebsocketStreamsPlaceholders('/<symbol>@avgPrice'.slice(1), {
                symbol,
                id,
            });
        },
        /**
         * Block Trade Streams push block trade information in real-time.
         *
         * Update Speed: Real-time
         *
         * @summary Block Trade Streams
         * @param {string} symbol Symbol to query
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        blockTrade: (symbol: string, id?: string): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('blockTrade', 'symbol', symbol);

            return replaceWebsocketStreamsPlaceholders('/<symbol>@blockTrade'.slice(1), {
                symbol,
                id,
            });
        },
        /**
         * Pushes any update to the best bid or ask's price or quantity in
         * real-time for a specified symbol.
         *
         * Multiple `<symbol>@bookTicker` streams can be subscribed to over one
         * connection.
         *
         * Update Speed: Real-time
         *
         * @summary Individual Symbol Book Ticker Streams
         * @param {string} symbol Symbol to query
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        bookTicker: (symbol: string, id?: string): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('bookTicker', 'symbol', symbol);

            return replaceWebsocketStreamsPlaceholders('/<symbol>@bookTicker'.slice(1), {
                symbol,
                id,
            });
        },
        /**
         * Order book price and quantity depth updates used to locally manage an order book.
         *
         * Update Speed: 1000ms or 100ms
         *
         * @summary Diff. Depth Stream
         * @param {string} symbol Symbol to query
         * @param {string} [id] Unique WebSocket request ID.
         * @param {DiffBookDepthUpdateSpeedEnum} [updateSpeed] Optional stream update speed suffix
         *
         * @throws {RequiredError}
         */
        diffBookDepth: (
            symbol: string,
            id?: string,
            updateSpeed?: DiffBookDepthUpdateSpeedEnum
        ): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('diffBookDepth', 'symbol', symbol);

            return replaceWebsocketStreamsPlaceholders('/<symbol>@depth@<updateSpeed>'.slice(1), {
                symbol,
                id,
                updateSpeed,
            });
        },
        /**
         * The Kline/Candlestick Stream push updates to the current
         * klines/candlestick every second in `UTC+0` timezone
         *
         * Update Speed: 1000ms for `1s`, 2000ms for the other intervals
         *
         * @summary Kline/Candlestick Streams for UTC
         * @param {string} symbol Symbol to query
         * @param {KlineIntervalEnum} interval
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        kline: (symbol: string, interval: KlineIntervalEnum, id?: string): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('kline', 'symbol', symbol);
            // verify required parameter 'interval' is not null or undefined
            assertParamExists('kline', 'interval', interval);

            return replaceWebsocketStreamsPlaceholders('/<symbol>@kline_<interval>'.slice(1), {
                symbol,
                interval,
                id,
            });
        },
        /**
         * The Kline/Candlestick Stream push updates to the current
         * klines/candlestick every second in `UTC+8` timezone
         *
         **Kline/Candlestick chart intervals:**
         *
         * Supported intervals: See Kline/Candlestick chart intervals
         *
         **UTC+8 timezone offset:**
         * - Kline intervals open and close in the UTC+8 timezone. For example the 1d klines will open at the beginning of the UTC+8 day, and close at the end of the UTC+8 day.
         * - Note that E (event time), t (start time) and T (close time) in the payload are Unix timestamps, which are always interpreted in UTC.
         *
         * Update Speed: 1000ms for `1s`, 2000ms for the other intervals
         *
         * @summary Kline/Candlestick Streams with timezone offset
         * @param {string} symbol Symbol to query
         * @param {KlineOffsetIntervalEnum} interval
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        klineOffset: (symbol: string, interval: KlineOffsetIntervalEnum, id?: string): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('klineOffset', 'symbol', symbol);
            // verify required parameter 'interval' is not null or undefined
            assertParamExists('klineOffset', 'interval', interval);

            return replaceWebsocketStreamsPlaceholders(
                '/<symbol>@kline_<interval>@+08:00'.slice(1),
                { symbol, interval, id }
            );
        },
        /**
         * 24hr rolling window mini-ticker statistics. These are NOT the statistics
         * of the UTC day, but a 24hr rolling window for the previous 24hrs.
         *
         * Update Speed: 1000ms
         *
         * @summary Individual Symbol Mini Ticker Stream
         * @param {string} symbol Symbol to query
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        miniTicker: (symbol: string, id?: string): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('miniTicker', 'symbol', symbol);

            return replaceWebsocketStreamsPlaceholders('/<symbol>@miniTicker'.slice(1), {
                symbol,
                id,
            });
        },
        /**
         * Top **\<levels\>** bids and asks, pushed every second.
         *
         * Update Speed: 1000ms or 100ms
         *
         * @summary WebSocket Partial Book Depth Streams
         * @param {string} symbol Symbol to query
         * @param {PartialBookDepthLevelsEnum} levels
         * @param {string} [id] Unique WebSocket request ID.
         * @param {PartialBookDepthUpdateSpeedEnum} [updateSpeed] Optional stream update speed suffix
         *
         * @throws {RequiredError}
         */
        partialBookDepth: (
            symbol: string,
            levels: PartialBookDepthLevelsEnum,
            id?: string,
            updateSpeed?: PartialBookDepthUpdateSpeedEnum
        ): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('partialBookDepth', 'symbol', symbol);
            // verify required parameter 'levels' is not null or undefined
            assertParamExists('partialBookDepth', 'levels', levels);

            return replaceWebsocketStreamsPlaceholders(
                '/<symbol>@depth<levels>@<updateSpeed>'.slice(1),
                { symbol, levels, id, updateSpeed }
            );
        },
        /**
         * Reference price stream for a symbol.
         *
         * Update Speed: 1000ms
         *
         * @summary Reference Price Streams
         * @param {string} symbol Symbol to query
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        referencePrice: (symbol: string, id?: string): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('referencePrice', 'symbol', symbol);

            return replaceWebsocketStreamsPlaceholders('/<symbol>@referencePrice'.slice(1), {
                symbol,
                id,
            });
        },
        /**
         * Rolling window ticker statistics for a single symbol, computed over
         * multiple windows.
         *
         **Note:** This stream is different from the `<symbol>@ticker` stream. The open time `"O"` always starts on a minute, while the closing time `"C"` is the current time
         * of the update. As such, the effective window might be up to 59999ms wider than `<window_size>`.
         *
         * Update Speed: 1000ms
         *
         * @summary Individual Symbol Rolling Window Statistics Streams
         * @param {string} symbol Symbol to query
         * @param {RollingWindowTickerWindowSizeEnum} windowSize
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        rollingWindowTicker: (
            symbol: string,
            windowSize: RollingWindowTickerWindowSizeEnum,
            id?: string
        ): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('rollingWindowTicker', 'symbol', symbol);
            // verify required parameter 'windowSize' is not null or undefined
            assertParamExists('rollingWindowTicker', 'windowSize', windowSize);

            return replaceWebsocketStreamsPlaceholders('/<symbol>@ticker_<windowSize>'.slice(1), {
                symbol,
                windowSize,
                id,
            });
        },
        /**
         * 24hr rolling window ticker statistics for a single symbol. These are NOT
         * the statistics of the UTC day, but a 24hr rolling window for the
         * previous 24hrs.
         *
         * Update Speed: 1000ms
         *
         * @summary Individual Symbol Ticker Streams
         * @param {string} symbol Symbol to query
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        ticker: (symbol: string, id?: string): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('ticker', 'symbol', symbol);

            return replaceWebsocketStreamsPlaceholders('/<symbol>@ticker'.slice(1), { symbol, id });
        },
        /**
         * The Trade Streams push raw trade information; each trade has a unique
         * buyer and seller.
         *
         * Update Speed: Real-time
         *
         * @summary Trade Streams
         * @param {string} symbol Symbol to query
         * @param {string} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        trade: (symbol: string, id?: string): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('trade', 'symbol', symbol);

            return replaceWebsocketStreamsPlaceholders('/<symbol>@trade'.slice(1), { symbol, id });
        },
    };
};

/**
 * Api - interface
 * @interface Api
 */
export interface ApiInterface {
    /**
     * The Aggregate Trade Streams push trade information that is aggregated
     * for a single taker order.
     *
     * Update Speed: Real-time
     *
     * @summary Aggregate Trade Streams
     * @param {AggTradeRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<AggTradeResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    aggTrade(requestParameters: AggTradeRequest): WebsocketStream<AggTradeResponse>;

    /**
     * Rolling window ticker statistics for all market symbols, computed over
     * multiple windows.
     *
     * Note that only tickers that have changed will be present in the array.
     *
     * Update Speed: 1000ms
     *
     * @summary All Market Rolling Window Statistics Streams
     * @param {AllMarketRollingWindowTickerRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<AllMarketRollingWindowTickerResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    allMarketRollingWindowTicker(
        requestParameters: AllMarketRollingWindowTickerRequest
    ): WebsocketStream<AllMarketRollingWindowTickerResponse>;

    /**
     * 24hr rolling window mini-ticker statistics for all symbols that changed
     * in an array. These are NOT the statistics of the UTC day, but a 24hr
     * rolling window for the previous 24hrs. Note that only tickers that have
     * changed will be present in the array.
     *
     * Update Speed: 1000ms
     *
     * @summary All Market Mini Tickers Stream
     * @param {AllMiniTickerRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<AllMiniTickerResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    allMiniTicker(requestParameters?: AllMiniTickerRequest): WebsocketStream<AllMiniTickerResponse>;

    /**
     * Average price streams push changes in the average price over a fixed time interval.
     *
     * Update Speed: 1000ms
     *
     * @summary Average Price
     * @param {AvgPriceRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<AvgPriceResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    avgPrice(requestParameters: AvgPriceRequest): WebsocketStream<AvgPriceResponse>;

    /**
     * Block Trade Streams push block trade information in real-time.
     *
     * Update Speed: Real-time
     *
     * @summary Block Trade Streams
     * @param {BlockTradeRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<BlockTradeResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    blockTrade(requestParameters: BlockTradeRequest): WebsocketStream<BlockTradeResponse>;

    /**
     * Pushes any update to the best bid or ask's price or quantity in
     * real-time for a specified symbol.
     *
     * Multiple `<symbol>@bookTicker` streams can be subscribed to over one
     * connection.
     *
     * Update Speed: Real-time
     *
     * @summary Individual Symbol Book Ticker Streams
     * @param {BookTickerRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<BookTickerResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    bookTicker(requestParameters: BookTickerRequest): WebsocketStream<BookTickerResponse>;

    /**
     * Order book price and quantity depth updates used to locally manage an order book.
     *
     * Update Speed: 1000ms or 100ms
     *
     * @summary Diff. Depth Stream
     * @param {DiffBookDepthRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<DiffBookDepthResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    diffBookDepth(requestParameters: DiffBookDepthRequest): WebsocketStream<DiffBookDepthResponse>;

    /**
     * The Kline/Candlestick Stream push updates to the current
     * klines/candlestick every second in `UTC+0` timezone
     *
     * Update Speed: 1000ms for `1s`, 2000ms for the other intervals
     *
     * @summary Kline/Candlestick Streams for UTC
     * @param {KlineRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<KlineResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    kline(requestParameters: KlineRequest): WebsocketStream<KlineResponse>;

    /**
     * The Kline/Candlestick Stream push updates to the current
     * klines/candlestick every second in `UTC+8` timezone
     *
     **Kline/Candlestick chart intervals:**
     *
     * Supported intervals: See Kline/Candlestick chart intervals
     *
     **UTC+8 timezone offset:**
     * - Kline intervals open and close in the UTC+8 timezone. For example the 1d klines will open at the beginning of the UTC+8 day, and close at the end of the UTC+8 day.
     * - Note that E (event time), t (start time) and T (close time) in the payload are Unix timestamps, which are always interpreted in UTC.
     *
     * Update Speed: 1000ms for `1s`, 2000ms for the other intervals
     *
     * @summary Kline/Candlestick Streams with timezone offset
     * @param {KlineOffsetRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<KlineOffsetResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    klineOffset(requestParameters: KlineOffsetRequest): WebsocketStream<KlineOffsetResponse>;

    /**
     * 24hr rolling window mini-ticker statistics. These are NOT the statistics
     * of the UTC day, but a 24hr rolling window for the previous 24hrs.
     *
     * Update Speed: 1000ms
     *
     * @summary Individual Symbol Mini Ticker Stream
     * @param {MiniTickerRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<MiniTickerResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    miniTicker(requestParameters: MiniTickerRequest): WebsocketStream<MiniTickerResponse>;

    /**
     * Top **\<levels\>** bids and asks, pushed every second.
     *
     * Update Speed: 1000ms or 100ms
     *
     * @summary WebSocket Partial Book Depth Streams
     * @param {PartialBookDepthRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<PartialBookDepthResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    partialBookDepth(
        requestParameters: PartialBookDepthRequest
    ): WebsocketStream<PartialBookDepthResponse>;

    /**
     * Reference price stream for a symbol.
     *
     * Update Speed: 1000ms
     *
     * @summary Reference Price Streams
     * @param {ReferencePriceRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<ReferencePriceResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    referencePrice(
        requestParameters: ReferencePriceRequest
    ): WebsocketStream<ReferencePriceResponse>;

    /**
     * Rolling window ticker statistics for a single symbol, computed over
     * multiple windows.
     *
     **Note:** This stream is different from the `<symbol>@ticker` stream. The open time `"O"` always starts on a minute, while the closing time `"C"` is the current time
     * of the update. As such, the effective window might be up to 59999ms wider than `<window_size>`.
     *
     * Update Speed: 1000ms
     *
     * @summary Individual Symbol Rolling Window Statistics Streams
     * @param {RollingWindowTickerRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<RollingWindowTickerResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    rollingWindowTicker(
        requestParameters: RollingWindowTickerRequest
    ): WebsocketStream<RollingWindowTickerResponse>;

    /**
     * 24hr rolling window ticker statistics for a single symbol. These are NOT
     * the statistics of the UTC day, but a 24hr rolling window for the
     * previous 24hrs.
     *
     * Update Speed: 1000ms
     *
     * @summary Individual Symbol Ticker Streams
     * @param {TickerRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<TickerResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    ticker(requestParameters: TickerRequest): WebsocketStream<TickerResponse>;

    /**
     * The Trade Streams push raw trade information; each trade has a unique
     * buyer and seller.
     *
     * Update Speed: Real-time
     *
     * @summary Trade Streams
     * @param {TradeRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<TradeResponse>}
     * @throws {RequiredError}
     * @memberof ApiInterface
     */
    trade(requestParameters: TradeRequest): WebsocketStream<TradeResponse>;
}

/**
 * Request parameters for aggTrade operation in Api.
 * @interface AggTradeRequest
 */
export interface AggTradeRequest {
    /**
     * Symbol to query
     * @type {string}
     * @memberof ApiAggTrade
     */
    readonly symbol: string;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiAggTrade
     */
    readonly id?: string;
}

/**
 * Request parameters for allMarketRollingWindowTicker operation in Api.
 * @interface AllMarketRollingWindowTickerRequest
 */
export interface AllMarketRollingWindowTickerRequest {
    /**
     *
     * @type {'1h' | '4h' | '1d'}
     * @memberof ApiAllMarketRollingWindowTicker
     */
    readonly windowSize: AllMarketRollingWindowTickerWindowSizeEnum;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiAllMarketRollingWindowTicker
     */
    readonly id?: string;
}

/**
 * Request parameters for allMiniTicker operation in Api.
 * @interface AllMiniTickerRequest
 */
export interface AllMiniTickerRequest {
    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiAllMiniTicker
     */
    readonly id?: string;
}

/**
 * Request parameters for avgPrice operation in Api.
 * @interface AvgPriceRequest
 */
export interface AvgPriceRequest {
    /**
     * Symbol to query
     * @type {string}
     * @memberof ApiAvgPrice
     */
    readonly symbol: string;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiAvgPrice
     */
    readonly id?: string;
}

/**
 * Request parameters for blockTrade operation in Api.
 * @interface BlockTradeRequest
 */
export interface BlockTradeRequest {
    /**
     * Symbol to query
     * @type {string}
     * @memberof ApiBlockTrade
     */
    readonly symbol: string;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiBlockTrade
     */
    readonly id?: string;
}

/**
 * Request parameters for bookTicker operation in Api.
 * @interface BookTickerRequest
 */
export interface BookTickerRequest {
    /**
     * Symbol to query
     * @type {string}
     * @memberof ApiBookTicker
     */
    readonly symbol: string;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiBookTicker
     */
    readonly id?: string;
}

/**
 * Request parameters for diffBookDepth operation in Api.
 * @interface DiffBookDepthRequest
 */
export interface DiffBookDepthRequest {
    /**
     * Symbol to query
     * @type {string}
     * @memberof ApiDiffBookDepth
     */
    readonly symbol: string;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiDiffBookDepth
     */
    readonly id?: string;

    /**
     * Optional stream update speed suffix
     * @type {'100ms'}
     * @memberof ApiDiffBookDepth
     */
    readonly updateSpeed?: DiffBookDepthUpdateSpeedEnum;
}

/**
 * Request parameters for kline operation in Api.
 * @interface KlineRequest
 */
export interface KlineRequest {
    /**
     * Symbol to query
     * @type {string}
     * @memberof ApiKline
     */
    readonly symbol: string;

    /**
     *
     * @type {'1s' | '1m' | '3m' | '5m' | '15m' | '30m' | '1h' | '2h' | '4h' | '6h' | '8h' | '12h' | '1d' | '3d' | '1w' | '1M'}
     * @memberof ApiKline
     */
    readonly interval: KlineIntervalEnum;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiKline
     */
    readonly id?: string;
}

/**
 * Request parameters for klineOffset operation in Api.
 * @interface KlineOffsetRequest
 */
export interface KlineOffsetRequest {
    /**
     * Symbol to query
     * @type {string}
     * @memberof ApiKlineOffset
     */
    readonly symbol: string;

    /**
     *
     * @type {'1s' | '1m' | '3m' | '5m' | '15m' | '30m' | '1h' | '2h' | '4h' | '6h' | '8h' | '12h' | '1d' | '3d' | '1w' | '1M'}
     * @memberof ApiKlineOffset
     */
    readonly interval: KlineOffsetIntervalEnum;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiKlineOffset
     */
    readonly id?: string;
}

/**
 * Request parameters for miniTicker operation in Api.
 * @interface MiniTickerRequest
 */
export interface MiniTickerRequest {
    /**
     * Symbol to query
     * @type {string}
     * @memberof ApiMiniTicker
     */
    readonly symbol: string;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiMiniTicker
     */
    readonly id?: string;
}

/**
 * Request parameters for partialBookDepth operation in Api.
 * @interface PartialBookDepthRequest
 */
export interface PartialBookDepthRequest {
    /**
     * Symbol to query
     * @type {string}
     * @memberof ApiPartialBookDepth
     */
    readonly symbol: string;

    /**
     *
     * @type {'5' | '10' | '20'}
     * @memberof ApiPartialBookDepth
     */
    readonly levels: PartialBookDepthLevelsEnum;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiPartialBookDepth
     */
    readonly id?: string;

    /**
     * Optional stream update speed suffix
     * @type {'100ms'}
     * @memberof ApiPartialBookDepth
     */
    readonly updateSpeed?: PartialBookDepthUpdateSpeedEnum;
}

/**
 * Request parameters for referencePrice operation in Api.
 * @interface ReferencePriceRequest
 */
export interface ReferencePriceRequest {
    /**
     * Symbol to query
     * @type {string}
     * @memberof ApiReferencePrice
     */
    readonly symbol: string;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiReferencePrice
     */
    readonly id?: string;
}

/**
 * Request parameters for rollingWindowTicker operation in Api.
 * @interface RollingWindowTickerRequest
 */
export interface RollingWindowTickerRequest {
    /**
     * Symbol to query
     * @type {string}
     * @memberof ApiRollingWindowTicker
     */
    readonly symbol: string;

    /**
     *
     * @type {'1h' | '4h' | '1d'}
     * @memberof ApiRollingWindowTicker
     */
    readonly windowSize: RollingWindowTickerWindowSizeEnum;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiRollingWindowTicker
     */
    readonly id?: string;
}

/**
 * Request parameters for ticker operation in Api.
 * @interface TickerRequest
 */
export interface TickerRequest {
    /**
     * Symbol to query
     * @type {string}
     * @memberof ApiTicker
     */
    readonly symbol: string;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiTicker
     */
    readonly id?: string;
}

/**
 * Request parameters for trade operation in Api.
 * @interface TradeRequest
 */
export interface TradeRequest {
    /**
     * Symbol to query
     * @type {string}
     * @memberof ApiTrade
     */
    readonly symbol: string;

    /**
     * Unique WebSocket request ID.
     * @type {string}
     * @memberof ApiTrade
     */
    readonly id?: string;
}

/**
 * Api - interface
 * @class Api
 * @extends {WebsocketStreamsBase}
 */
export class Api implements ApiInterface {
    private readonly websocketBase: WebsocketStreamsBase;
    private localVarParamCreator;

    constructor(websocketBase: WebsocketStreamsBase) {
        this.websocketBase = websocketBase;
        this.localVarParamCreator = ApiParamCreator();
    }

    /**
     * The Aggregate Trade Streams push trade information that is aggregated
     * for a single taker order.
     *
     * Update Speed: Real-time
     *
     * @summary Aggregate Trade Streams
     * @param {AggTradeRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<AggTradeResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#agg-trade Binance API Documentation}
     */
    public aggTrade(requestParameters: AggTradeRequest): WebsocketStream<AggTradeResponse> {
        const stream = this.localVarParamCreator.aggTrade(
            requestParameters?.symbol,
            requestParameters?.id
        );

        return createStreamHandler<AggTradeResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * Rolling window ticker statistics for all market symbols, computed over
     * multiple windows.
     *
     * Note that only tickers that have changed will be present in the array.
     *
     * Update Speed: 1000ms
     *
     * @summary All Market Rolling Window Statistics Streams
     * @param {AllMarketRollingWindowTickerRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<AllMarketRollingWindowTickerResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#all-market-rolling-window-ticker Binance API Documentation}
     */
    public allMarketRollingWindowTicker(
        requestParameters: AllMarketRollingWindowTickerRequest
    ): WebsocketStream<AllMarketRollingWindowTickerResponse> {
        const stream = this.localVarParamCreator.allMarketRollingWindowTicker(
            requestParameters?.windowSize,
            requestParameters?.id
        );

        return createStreamHandler<AllMarketRollingWindowTickerResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * 24hr rolling window mini-ticker statistics for all symbols that changed
     * in an array. These are NOT the statistics of the UTC day, but a 24hr
     * rolling window for the previous 24hrs. Note that only tickers that have
     * changed will be present in the array.
     *
     * Update Speed: 1000ms
     *
     * @summary All Market Mini Tickers Stream
     * @param {AllMiniTickerRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<AllMiniTickerResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#all-mini-ticker Binance API Documentation}
     */
    public allMiniTicker(
        requestParameters: AllMiniTickerRequest = {}
    ): WebsocketStream<AllMiniTickerResponse> {
        const stream = this.localVarParamCreator.allMiniTicker(requestParameters?.id);

        return createStreamHandler<AllMiniTickerResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * Average price streams push changes in the average price over a fixed time interval.
     *
     * Update Speed: 1000ms
     *
     * @summary Average Price
     * @param {AvgPriceRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<AvgPriceResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#avg-price Binance API Documentation}
     */
    public avgPrice(requestParameters: AvgPriceRequest): WebsocketStream<AvgPriceResponse> {
        const stream = this.localVarParamCreator.avgPrice(
            requestParameters?.symbol,
            requestParameters?.id
        );

        return createStreamHandler<AvgPriceResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * Block Trade Streams push block trade information in real-time.
     *
     * Update Speed: Real-time
     *
     * @summary Block Trade Streams
     * @param {BlockTradeRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<BlockTradeResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#block-trade Binance API Documentation}
     */
    public blockTrade(requestParameters: BlockTradeRequest): WebsocketStream<BlockTradeResponse> {
        const stream = this.localVarParamCreator.blockTrade(
            requestParameters?.symbol,
            requestParameters?.id
        );

        return createStreamHandler<BlockTradeResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * Pushes any update to the best bid or ask's price or quantity in
     * real-time for a specified symbol.
     *
     * Multiple `<symbol>@bookTicker` streams can be subscribed to over one
     * connection.
     *
     * Update Speed: Real-time
     *
     * @summary Individual Symbol Book Ticker Streams
     * @param {BookTickerRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<BookTickerResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#book-ticker Binance API Documentation}
     */
    public bookTicker(requestParameters: BookTickerRequest): WebsocketStream<BookTickerResponse> {
        const stream = this.localVarParamCreator.bookTicker(
            requestParameters?.symbol,
            requestParameters?.id
        );

        return createStreamHandler<BookTickerResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * Order book price and quantity depth updates used to locally manage an order book.
     *
     * Update Speed: 1000ms or 100ms
     *
     * @summary Diff. Depth Stream
     * @param {DiffBookDepthRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<DiffBookDepthResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#diff-book-depth Binance API Documentation}
     */
    public diffBookDepth(
        requestParameters: DiffBookDepthRequest
    ): WebsocketStream<DiffBookDepthResponse> {
        const stream = this.localVarParamCreator.diffBookDepth(
            requestParameters?.symbol,
            requestParameters?.id,
            requestParameters?.updateSpeed
        );

        return createStreamHandler<DiffBookDepthResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * The Kline/Candlestick Stream push updates to the current
     * klines/candlestick every second in `UTC+0` timezone
     *
     * Update Speed: 1000ms for `1s`, 2000ms for the other intervals
     *
     * @summary Kline/Candlestick Streams for UTC
     * @param {KlineRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<KlineResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#kline Binance API Documentation}
     */
    public kline(requestParameters: KlineRequest): WebsocketStream<KlineResponse> {
        const stream = this.localVarParamCreator.kline(
            requestParameters?.symbol,
            requestParameters?.interval,
            requestParameters?.id
        );

        return createStreamHandler<KlineResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * The Kline/Candlestick Stream push updates to the current
     * klines/candlestick every second in `UTC+8` timezone
     *
     **Kline/Candlestick chart intervals:**
     *
     * Supported intervals: See Kline/Candlestick chart intervals
     *
     **UTC+8 timezone offset:**
     * - Kline intervals open and close in the UTC+8 timezone. For example the 1d klines will open at the beginning of the UTC+8 day, and close at the end of the UTC+8 day.
     * - Note that E (event time), t (start time) and T (close time) in the payload are Unix timestamps, which are always interpreted in UTC.
     *
     * Update Speed: 1000ms for `1s`, 2000ms for the other intervals
     *
     * @summary Kline/Candlestick Streams with timezone offset
     * @param {KlineOffsetRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<KlineOffsetResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#kline-offset Binance API Documentation}
     */
    public klineOffset(
        requestParameters: KlineOffsetRequest
    ): WebsocketStream<KlineOffsetResponse> {
        const stream = this.localVarParamCreator.klineOffset(
            requestParameters?.symbol,
            requestParameters?.interval,
            requestParameters?.id
        );

        return createStreamHandler<KlineOffsetResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * 24hr rolling window mini-ticker statistics. These are NOT the statistics
     * of the UTC day, but a 24hr rolling window for the previous 24hrs.
     *
     * Update Speed: 1000ms
     *
     * @summary Individual Symbol Mini Ticker Stream
     * @param {MiniTickerRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<MiniTickerResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#mini-ticker Binance API Documentation}
     */
    public miniTicker(requestParameters: MiniTickerRequest): WebsocketStream<MiniTickerResponse> {
        const stream = this.localVarParamCreator.miniTicker(
            requestParameters?.symbol,
            requestParameters?.id
        );

        return createStreamHandler<MiniTickerResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * Top **\<levels\>** bids and asks, pushed every second.
     *
     * Update Speed: 1000ms or 100ms
     *
     * @summary WebSocket Partial Book Depth Streams
     * @param {PartialBookDepthRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<PartialBookDepthResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#partial-book-depth Binance API Documentation}
     */
    public partialBookDepth(
        requestParameters: PartialBookDepthRequest
    ): WebsocketStream<PartialBookDepthResponse> {
        const stream = this.localVarParamCreator.partialBookDepth(
            requestParameters?.symbol,
            requestParameters?.levels,
            requestParameters?.id,
            requestParameters?.updateSpeed
        );

        return createStreamHandler<PartialBookDepthResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * Reference price stream for a symbol.
     *
     * Update Speed: 1000ms
     *
     * @summary Reference Price Streams
     * @param {ReferencePriceRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<ReferencePriceResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#reference-price Binance API Documentation}
     */
    public referencePrice(
        requestParameters: ReferencePriceRequest
    ): WebsocketStream<ReferencePriceResponse> {
        const stream = this.localVarParamCreator.referencePrice(
            requestParameters?.symbol,
            requestParameters?.id
        );

        return createStreamHandler<ReferencePriceResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * Rolling window ticker statistics for a single symbol, computed over
     * multiple windows.
     *
     **Note:** This stream is different from the `<symbol>@ticker` stream. The open time `"O"` always starts on a minute, while the closing time `"C"` is the current time
     * of the update. As such, the effective window might be up to 59999ms wider than `<window_size>`.
     *
     * Update Speed: 1000ms
     *
     * @summary Individual Symbol Rolling Window Statistics Streams
     * @param {RollingWindowTickerRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<RollingWindowTickerResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#rolling-window-ticker Binance API Documentation}
     */
    public rollingWindowTicker(
        requestParameters: RollingWindowTickerRequest
    ): WebsocketStream<RollingWindowTickerResponse> {
        const stream = this.localVarParamCreator.rollingWindowTicker(
            requestParameters?.symbol,
            requestParameters?.windowSize,
            requestParameters?.id
        );

        return createStreamHandler<RollingWindowTickerResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * 24hr rolling window ticker statistics for a single symbol. These are NOT
     * the statistics of the UTC day, but a 24hr rolling window for the
     * previous 24hrs.
     *
     * Update Speed: 1000ms
     *
     * @summary Individual Symbol Ticker Streams
     * @param {TickerRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<TickerResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#ticker Binance API Documentation}
     */
    public ticker(requestParameters: TickerRequest): WebsocketStream<TickerResponse> {
        const stream = this.localVarParamCreator.ticker(
            requestParameters?.symbol,
            requestParameters?.id
        );

        return createStreamHandler<TickerResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }

    /**
     * The Trade Streams push raw trade information; each trade has a unique
     * buyer and seller.
     *
     * Update Speed: Real-time
     *
     * @summary Trade Streams
     * @param {TradeRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<TradeResponse>}
     * @throws {RequiredError}
     * @memberof Api
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#trade Binance API Documentation}
     */
    public trade(requestParameters: TradeRequest): WebsocketStream<TradeResponse> {
        const stream = this.localVarParamCreator.trade(
            requestParameters?.symbol,
            requestParameters?.id
        );

        return createStreamHandler<TradeResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id
        );
    }
}

export enum AllMarketRollingWindowTickerWindowSizeEnum {
    WINDOW_SIZE_1h = '1h',
    WINDOW_SIZE_4h = '4h',
    WINDOW_SIZE_1d = '1d',
}

export enum DiffBookDepthUpdateSpeedEnum {
    UPDATE_SPEED_100ms = '100ms',
}

export enum KlineIntervalEnum {
    INTERVAL_1s = '1s',
    INTERVAL_1m = '1m',
    INTERVAL_3m = '3m',
    INTERVAL_5m = '5m',
    INTERVAL_15m = '15m',
    INTERVAL_30m = '30m',
    INTERVAL_1h = '1h',
    INTERVAL_2h = '2h',
    INTERVAL_4h = '4h',
    INTERVAL_6h = '6h',
    INTERVAL_8h = '8h',
    INTERVAL_12h = '12h',
    INTERVAL_1d = '1d',
    INTERVAL_3d = '3d',
    INTERVAL_1w = '1w',
    INTERVAL_1M = '1M',
}

export enum KlineOffsetIntervalEnum {
    INTERVAL_1s = '1s',
    INTERVAL_1m = '1m',
    INTERVAL_3m = '3m',
    INTERVAL_5m = '5m',
    INTERVAL_15m = '15m',
    INTERVAL_30m = '30m',
    INTERVAL_1h = '1h',
    INTERVAL_2h = '2h',
    INTERVAL_4h = '4h',
    INTERVAL_6h = '6h',
    INTERVAL_8h = '8h',
    INTERVAL_12h = '12h',
    INTERVAL_1d = '1d',
    INTERVAL_3d = '3d',
    INTERVAL_1w = '1w',
    INTERVAL_1M = '1M',
}

export enum PartialBookDepthLevelsEnum {
    LEVELS_5 = '5',
    LEVELS_10 = '10',
    LEVELS_20 = '20',
}

export enum PartialBookDepthUpdateSpeedEnum {
    UPDATE_SPEED_100ms = '100ms',
}

export enum RollingWindowTickerWindowSizeEnum {
    WINDOW_SIZE_1h = '1h',
    WINDOW_SIZE_4h = '4h',
    WINDOW_SIZE_1d = '1d',
}
